import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, brl, formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, UploadSimple, Image as ImageIcon, Camera } from "@phosphor-icons/react";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [commCats, setCommCats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ codigo: "", descricao: "", unidade: "UN", prices: {}, categoria_comissao: "Padrão" });
  const [importOpen, setImportOpen] = useState(false);
  const [importTable, setImportTable] = useState("");
  const [importingCatalog, setImportingCatalog] = useState(false);
  const fileRef = useRef();
  const imgRef = useRef();
  const [uploadingImg, setUploadingImg] = useState(false);

  const load = useCallback(async () => {
    setProducts((await api.get("/products", { params: q ? { q } : {} })).data);
  }, [q]);
  const loadTables = useCallback(async () => {
    setTables((await api.get("/price-tables")).data);
  }, []);
  const loadCommCats = useCallback(async () => {
    setCommCats((await api.get("/commission/categories")).data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { loadCommCats(); }, [loadCommCats]);

  const openNew = () => { setEditing(null); setForm({ codigo: "", descricao: "", unidade: "UN", prices: {}, categoria_comissao: "Padrão" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ codigo: p.codigo, descricao: p.descricao, unidade: p.unidade || "UN", prices: p.prices || {}, categoria_comissao: p.categoria_comissao || "Padrão" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { codigo: form.codigo, descricao: form.descricao, unidade: form.unidade, prices: Object.fromEntries(Object.entries(form.prices).map(([k, v]) => [k, Number(v) || 0])) };
      let prodId = editing?.id;
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else { const { data } = await api.post("/products", payload); prodId = data.id; }
      if (prodId) await api.put(`/products/${prodId}/commission-category`, { categoria: form.categoria_comissao || "Padrão" });
      toast.success("Produto salvo."); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (p) => { if (!window.confirm(`Excluir ${p.codigo}?`)) return; await api.delete(`/products/${p.id}`); toast.success("Excluído."); load(); };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !editing) return;
    setUploadingImg(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/products/${editing.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing(data);
      setProducts((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      toast.success("Foto enviada.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadingImg(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingCatalog(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/products/import-catalog`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importado: ${data.created} novos, ${data.updated} atualizados. Tabelas: ${data.price_tables.join(", ") || "—"}.`);
      setImportOpen(false); load(); loadTables();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setImportingCatalog(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Produtos</h1>
          <p className="text-muted-foreground mt-1">Catálogo e preços por tabela.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-products-btn"><UploadSimple size={18} weight="bold" /> Importar Excel</Button>
          <Button onClick={openNew} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-product-btn"><Plus size={18} weight="bold" /> Novo Produto</Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar por código ou descrição..." className="pl-10" data-testid="products-search" />
      </div>

      <Card className="shadow-none border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Foto</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Código</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Descrição</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Comissão</th>
              {tables.map((t) => <th key={t.id} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-zinc-500">{t.name}</th>)}
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {products.length === 0 && <tr><td colSpan={5 + tables.length} className="px-4 py-12 text-center text-muted-foreground">Nenhum produto. Importe um Excel ou cadastre.</td></tr>}
              {products.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50" data-testid={`product-row-${p.codigo}`}>
                  <td className="px-4 py-2">
                    {p.image_path ? (
                      <img src={`${API}/products/${p.id}/image?v=${encodeURIComponent(p.image_path)}`} alt={p.descricao} className="h-10 w-10 rounded object-cover border border-border" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-300"><ImageIcon size={18} /></div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-700">{p.codigo}</td>
                  <td className="px-4 py-3 text-zinc-800 max-w-[280px] truncate">{p.descricao}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{p.categoria_comissao || "Padrão"}</Badge></td>
                  {tables.map((t) => <td key={t.id} className="px-4 py-3 text-right tabular-nums text-zinc-700">{p.prices?.[t.id] != null ? brl(p.prices[t.id]) : "—"}</td>)}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-accent mr-3" data-testid={`edit-product-${p.codigo}`}><PencilSimple size={18} /></button>
                    <button onClick={() => remove(p)} className="text-zinc-400 hover:text-destructive"><Trash size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {editing && editing.image_path ? (
                <img src={`${API}/products/${editing.id}/image?v=${encodeURIComponent(editing.image_path)}`} alt="" className="h-20 w-20 rounded-md object-cover border border-border" />
              ) : (
                <div className="h-20 w-20 rounded-md bg-zinc-100 flex items-center justify-center text-zinc-300"><ImageIcon size={28} /></div>
              )}
              <div>
                {editing ? (
                  <>
                    <Button type="button" variant="outline" size="sm" className="gap-2" disabled={uploadingImg} onClick={() => imgRef.current?.click()} data-testid="upload-product-image-btn">
                      <Camera size={16} /> {uploadingImg ? "Enviando..." : editing.image_path ? "Trocar foto" : "Adicionar foto"}
                    </Button>
                    <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="product-image-input" />
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP — máx 5MB.</p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">Salve o produto primeiro para adicionar uma foto.</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid="product-codigo" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs uppercase text-zinc-500">Unidade</Label><Input value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Descrição</Label><Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="product-descricao" /></div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Categoria de comissão</Label>
              <Select value={form.categoria_comissao} onValueChange={(v) => setForm({ ...form, categoria_comissao: v })}>
                <SelectTrigger data-testid="product-comm-category"><SelectValue placeholder="Padrão" /></SelectTrigger>
                <SelectContent>
                  {commCats.map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="text-xs uppercase text-zinc-500">Preços por tabela</Label>
              {tables.map((t) => (
                <div key={t.id} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-700 w-40 truncate">{t.name}</span>
                  <Input type="number" step="0.01" value={form.prices[t.id] ?? ""} onChange={(e) => setForm({ ...form, prices: { ...form.prices, [t.id]: e.target.value } })} placeholder="0,00" className="text-right" />
                </div>
              ))}
              {tables.length === 0 && <p className="text-sm text-muted-foreground">Cadastre tabelas de preço primeiro.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-product-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importar catálogo (Excel)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Aceita <b>.xlsb</b> e <b>.xlsx</b>. O sistema lê todas as abas, usa as colunas <b>Cód</b> e <b>Nome/Linha</b>, e cria/atualiza as tabelas de preço a partir das colunas de prazo (ex: <b>14 DIAS</b>, <b>30 DIAS</b>, <b>60 DIAS</b>, <b>90 DIAS</b>, <b>PROMO</b>). Produtos existentes (mesmo código) têm os preços atualizados.
          </p>
          <input ref={fileRef} type="file" accept=".xlsb,.xlsx,.xls" onChange={doImport} disabled={importingCatalog} className="text-sm" data-testid="import-file-input" />
          {importingCatalog && <p className="text-sm text-accent">Importando catálogo, aguarde...</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
